import { generateRequestId, getApiBaseUrl } from '@lib/utils';
import type { AccessMatrix, ApiError, AuditLogEntry, Booking, Center, CentersOverview, PaginatedResponse, Room, SlotOverride, CreateSlotOverrideResponse, DashboardSummary, SchedulesWeek, Passkey, ReportByCenterRow, ReportByDayRow, ReportRange, StaffAbsence, StaffAbsenceReason, SystemHealth, SystemError, WorkerMetrics } from './types';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Get the stored auth token from localStorage.
 * Abstracted so callers never touch localStorage directly.
 */
function getAuthToken(): string | null {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
}

/**
 * Read the active centre filter from sessionStorage. The write happens in
 * `useActiveCenter`'s provider (same key). When the stored value is `'all'`
 * we send NO header — backend falls back to the role's allowed-set
 * behaviour (superadmin = all, center_admin = adminCenterIds ∪ cid,
 * staff = cid). When it's a specific centre id we forward it as
 * `X-Center-Id`.
 *
 * Keep in sync with `ACTIVE_CENTER_STORAGE_KEY` in
 * `frontend/src/hooks/useActiveCenter.tsx`. Duplicated here to avoid
 * pulling React into the fetch layer.
 */
function getActiveCenterId(): string | null {
  try {
    const raw = sessionStorage.getItem('admin_active_center_id');
    if (!raw || raw === 'all') return null;
    return raw;
  } catch {
    return null;
  }
}

/**
 * Core fetch wrapper with:
 * - Auth header injection (Bearer token)
 * - Request-ID tracing
 * - Structured error parsing
 * - Type-safe response
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  const { token: overrideToken, ...fetchOptions } = options;
  const base = getApiBaseUrl();
  const url = `${base}${path}`;

  const token = overrideToken ?? getAuthToken();
  const requestId = generateRequestId();

  const headers = new Headers(fetchOptions.headers);
  if (!(fetchOptions.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('X-Request-ID', requestId);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Forward the active centre selected via the per-section dropdown shared
  // state (`useActiveCenter` + sessionStorage). `'all'` is encoded as the
  // absence of the header so the backend uses its role-specific defaults.
  if (!headers.has('X-Center-Id')) {
    const activeCenterId = getActiveCenterId();
    if (activeCenterId) headers.set('X-Center-Id', activeCenterId);
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    let errorBody: ApiError = { error: response.statusText };
    try {
      errorBody = (await response.json()) as ApiError;
    } catch {
      // ignore JSON parse failure, use statusText
    }
    // Token expired or invalid — clear session and redirect to login.
    // Skip redirect for the auth sign-in endpoints so flow errors surface in the UI.
    if (response.status === 401 && !path.includes('/sign-in/')) {
      try { localStorage.removeItem('auth_token'); } catch { /* */ }
      try { localStorage.removeItem('auth_user'); } catch { /* */ }
      window.location.href = '/admin/login';
    }
    throw new ApiRequestError(
      errorBody.message ?? errorBody.error,
      response.status,
      errorBody.code ?? errorBody.error,
      errorBody.details,
    );
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Admin auth — passwordless (BetterAuth magic link)
// ---------------------------------------------------------------------------
export interface MagicVerifyResult {
  token: string;
  user: { id: string; email: string; name: string | null; centerId: string | null; role: string };
}

export const authApi = {
  /**
   * Request a magic-link email. Invite-only on the server (`disableSignUp`):
   * unknown emails get the same 200 response (no enumeration).
   */
  requestMagicLink: (email: string) =>
    apiFetch<{ status: boolean }>('/api/auth/sign-in/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * Exchange a magic-link token for a session. Called WITHOUT `callbackURL` so
   * BetterAuth returns `{ token, user, session }` as JSON on success. On an
   * invalid/expired token it issues a 302 redirect — caught via `redirect:
   * 'manual'` (opaque response → not ok) and surfaced as an error.
   */
  verifyMagicLink: async (token: string): Promise<MagicVerifyResult> => {
    const base = getApiBaseUrl();
    const url = `${base}/api/auth/magic-link/verify?token=${encodeURIComponent(token)}`;
    const res = await fetch(url, {
      method: 'GET',
      redirect: 'manual',
      headers: { 'X-Request-ID': generateRequestId() },
    });
    if (!res.ok) {
      throw new ApiRequestError(
        'El enlace no es válido o ha caducado.',
        res.status || 400,
        'INVALID_MAGIC_LINK',
      );
    }
    return res.json() as Promise<MagicVerifyResult>;
  },

  /** Revoke the current session server-side. Best-effort — ignores failure. */
  signOut: () =>
    apiFetch<{ success?: boolean }>('/api/auth/sign-out', { method: 'POST' }).catch(() => undefined),
};

// ---------------------------------------------------------------------------
// Passkey (WebAuthn) — @better-auth/passkey
// ---------------------------------------------------------------------------
export interface PasskeyAuthenticateResult {
  token: string;
  user: { id: string; email: string; name: string | null; centerId: string | null; role: string };
}

export const passkeyApi = {
  /** Challenge + options for registering a new passkey (requires active session). */
  registerOptions: (params?: { name?: string; authenticatorAttachment?: 'platform' | 'cross-platform' }) => {
    const qs = new URLSearchParams();
    if (params?.name) qs.set('name', params.name);
    if (params?.authenticatorAttachment) qs.set('authenticatorAttachment', params.authenticatorAttachment);
    const q = qs.toString();
    return apiFetch<PublicKeyCredentialCreationOptionsJSON>(
      `/api/auth/passkey/generate-register-options${q ? `?${q}` : ''}`,
    );
  },

  /** Verify the attestation response from `navigator.credentials.create`. */
  registerVerify: (body: { response: unknown; name?: string }) =>
    apiFetch<Passkey>('/api/auth/passkey/verify-registration', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Challenge + options for signing in with a passkey (no session needed). */
  authenticateOptions: () =>
    apiFetch<PublicKeyCredentialRequestOptionsJSON>(
      '/api/auth/passkey/generate-authenticate-options',
    ),

  /** Verify the assertion response from `navigator.credentials.get` — issues a session.
   *
   * The passkey plugin's verify endpoint returns `{ session: { token, ... }, user: {...} }`,
   * NOT the flat `{ token, user }` shape the magic-link verify returns. Additionally the
   * bearer plugin attaches the session token to a `set-auth-token` response header — we
   * prefer the header when present (more robust) and fall back to the body. */
  authenticateVerify: async (body: { response: unknown }): Promise<PasskeyAuthenticateResult> => {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/auth/passkey/verify-authentication`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-ID': generateRequestId(),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let code = 'PASSKEY_AUTH_FAILED';
      try {
        const parsed = (await res.json()) as ApiError;
        code = parsed.code ?? parsed.error;
      } catch { /* */ }
      throw new ApiRequestError('No pudimos iniciar sesión con este passkey.', res.status, code);
    }
    const headerToken = res.headers.get('set-auth-token') ?? res.headers.get('x-auth-token');
    const json = (await res.json()) as {
      token?: string;
      user?: PasskeyAuthenticateResult['user'];
      session?: { token?: string; user?: PasskeyAuthenticateResult['user'] };
    };
    const token = headerToken ?? json.token ?? json.session?.token;
    const user = json.user ?? json.session?.user;
    if (!token || !user) {
      throw new ApiRequestError(
        'Respuesta inesperada del servidor de passkey.',
        res.status,
        'PASSKEY_BAD_RESPONSE',
      );
    }
    return { token, user };
  },

  list: () => apiFetch<Passkey[]>('/api/auth/passkey/list-user-passkeys'),

  delete: (id: string) =>
    apiFetch<{ status: boolean }>('/api/auth/passkey/delete-passkey', {
      method: 'POST',
      body: JSON.stringify({ id }),
    }),

  rename: (id: string, name: string) =>
    apiFetch<{ passkey: Passkey }>('/api/auth/passkey/update-passkey', {
      method: 'POST',
      body: JSON.stringify({ id, name }),
    }),
};

// Minimal local types — the full @simplewebauthn types are heavy and only
// the shape is needed by the client.
type PublicKeyCredentialCreationOptionsJSON = Record<string, unknown>;
type PublicKeyCredentialRequestOptionsJSON = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Admin bookings
// ---------------------------------------------------------------------------
// B-10 closeout (parcial): tipos reales en respuestas en vez de `<unknown>`.
// Las firmas siguen siendo compatibles porque consumidores que casteen al
// vuelo pasan a recibir un tipo concreto sin sorpresas. Los endpoints PUT
// devuelven el booking actualizado (`Booking`) — antes era `unknown` y los
// callers desreferenciaban a ciegas. `list` ahora devuelve
// `PaginatedResponse<Booking>` para que el llamante no tenga que castear.
export const adminBookingsApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch<PaginatedResponse<Booking>>(`/api/admin/bookings?${qs}`);
  },
  get: (id: string) => apiFetch<Booking>(`/api/admin/bookings/${id}`),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  cancel: (id: string, reason?: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'cancelled', ...(reason ? { staffNote: reason } : {}) }),
    }),
  complete: (id: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'completed' }),
    }),
  checkIn: (id: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'checked_in' }),
    }),
  noShow: (id: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'noshow' }),
    }),
  approve: (id: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'confirmed' }),
    }),
  reject: (id: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'rejected' }),
    }),
  reschedule: (id: string, newStartTime: string, staffId?: string) =>
    apiFetch<Booking>(`/api/admin/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ startTime: newStartTime, ...(staffId ? { staffId } : {}) }),
    }),
  history: (id: string) =>
    apiFetch<AuditLogEntry[]>(`/api/admin/bookings/${id}/history`),
};

export const adminDashboardApi = {
  /** PR #5: filtros opcionales por centro / staff. Sin params devuelve
   *  el agregado según rol del JWT (super=todos, center_admin=allowed). */
  get: (params: { centerId?: string; staffId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.centerId) qs.set('centerId', params.centerId);
    if (params.staffId) qs.set('staffId', params.staffId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<DashboardSummary>(`/api/admin/dashboard${suffix}`);
  },
};

export const adminSystemApi = {
  health: () => apiFetch<SystemHealth>('/api/admin/system/health'),
  errors: (limit = 20) => apiFetch<SystemError[]>(`/api/admin/system/errors?limit=${String(limit)}`),
  metrics: () => apiFetch<WorkerMetrics>('/api/admin/system/metrics'),
  accessMatrix: () => apiFetch<AccessMatrix>('/api/admin/system/access-matrix'),
};

export const adminStaffSchedulesApi = {
  listWeek: (weekStart: string) =>
    apiFetch<SchedulesWeek>(`/api/admin/staff-schedules?weekStart=${weekStart}`),
  upsert: (data: { staffId: string; centerId: string; weekStart: string; dayOfWeek: number; timeRange: string }) =>
    apiFetch<{ id: string; staffId: string; centerId: string; weekStart: string; dayOfWeek: number; timeRange: string }>(
      '/api/admin/staff-schedules',
      { method: 'PUT', body: JSON.stringify(data) },
    ),
  clear: (staffId: string, centerId: string, weekStart: string, dayOfWeek: number) =>
    apiFetch<{ removed: boolean }>(
      `/api/admin/staff-schedules?staffId=${staffId}&centerId=${centerId}&weekStart=${weekStart}&dayOfWeek=${String(dayOfWeek)}`,
      { method: 'DELETE' },
    ),
  copy: (fromWeekStart: string, toWeekStart: string) =>
    apiFetch<{ copied: number }>('/api/admin/staff-schedules/copy', {
      method: 'POST',
      body: JSON.stringify({ fromWeekStart, toWeekStart }),
    }),
};

export const adminReportsApi = {
  byCenter: (range: ReportRange = '90d') =>
    apiFetch<ReportByCenterRow[]>(`/api/admin/reports/by-center?range=${range}`),
  byDay: (range: ReportRange = '90d') =>
    apiFetch<ReportByDayRow[]>(`/api/admin/reports/by-day?range=${range}`),
  exportBookingsCsv: async (from: string, to: string): Promise<void> => {
    const token = getAuthToken();
    const url = `/api/admin/reports/bookings?from=${from}&to=${to}`;
    const res = await fetch(`${getApiBaseUrl()}${url}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new ApiRequestError('Export failed', res.status);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `reservas-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(href);
  },
};

// ---------------------------------------------------------------------------
// Admin customers
// ---------------------------------------------------------------------------
export const adminCustomersApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch<unknown>(`/api/admin/customers?${qs}`);
  },
  get: (id: string) => apiFetch<unknown>(`/api/admin/customers/${id}`),
  /** Create a customer in the active centre. Backend de-dupes by email/phone. */
  create: (data: { name: string; email: string; phone: string }) =>
    apiFetch<unknown>(`/api/admin/customers`, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/admin/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /** Re-points every booking from `sourceCustomerId` onto `targetCustomerId`,
   *  soft-deletes the source. Returns `{ bookingsMoved }`. The optional
   *  `overrides` lets the operator choose per-field whether the target's or
   *  the source's value wins for `name`/`email`/`phone`/`internalNote`.
   *  Omitted fields default to `target`. */
  merge: (
    targetCustomerId: string,
    sourceCustomerId: string,
    overrides?: Partial<Record<'name' | 'email' | 'phone' | 'internalNote', 'target' | 'source'>>,
  ) =>
    apiFetch<{ bookingsMoved: number }>(
      `/api/admin/customers/${targetCustomerId}/merge`,
      {
        method: 'POST',
        body: JSON.stringify(overrides ? { sourceCustomerId, overrides } : { sourceCustomerId }),
      },
    ),
};

// ---------------------------------------------------------------------------
// Admin services
// ---------------------------------------------------------------------------
export const adminServicesApi = {
  list: async () => {
    const resp = await apiFetch<{ data: unknown[] }>('/api/admin/services');
    return resp.data;
  },
  create: (data: Record<string, unknown>) =>
    apiFetch<unknown>('/api/admin/services', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/admin/services/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiFetch<unknown>(`/api/admin/services/${id}`, { method: 'DELETE' }),
  getRooms: (id: string) =>
    apiFetch<{ serviceId: string; roomIds: string[] }>(`/api/admin/services/${id}/rooms`),
  setRooms: (id: string, roomIds: string[]) =>
    apiFetch<{ serviceId: string; roomIds: string[] }>(`/api/admin/services/${id}/rooms`, {
      method: 'PUT',
      body: JSON.stringify({ roomIds }),
    }),
  getStaff: (id: string) =>
    apiFetch<{ serviceId: string; staffIds: string[] }>(`/api/admin/services/${id}/staff`),
  setStaff: (id: string, staffIds: string[]) =>
    apiFetch<{ serviceId: string; staffIds: string[] }>(`/api/admin/services/${id}/staff`, {
      method: 'PUT',
      body: JSON.stringify({ staffIds }),
    }),
};

// ---------------------------------------------------------------------------
// Admin staff
// ---------------------------------------------------------------------------
export const adminStaffApi = {
  list: async () => {
    const resp = await apiFetch<{ data: unknown[] }>('/api/admin/staff');
    return resp.data;
  },
  create: (data: Record<string, unknown>) =>
    apiFetch<unknown>('/api/admin/staff', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/admin/staff/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiFetch<unknown>(`/api/admin/staff/${id}`, { method: 'DELETE' }),
  addCenter: (staffId: string, centerId: string) =>
    apiFetch<unknown>(`/api/admin/staff/${staffId}/centers`, {
      method: 'POST',
      body: JSON.stringify({ centerId }),
    }),
  removeCenter: (staffId: string, centerId: string) =>
    apiFetch<undefined>(`/api/admin/staff/${staffId}/centers/${centerId}`, { method: 'DELETE' }),
  getServices: (staffId: string) =>
    apiFetch<{ staffId: string; serviceIds: string[] }>(`/api/admin/staff/${staffId}/services`),
  setServices: (staffId: string, serviceIds: string[]) =>
    apiFetch<{ staffId: string; serviceIds: string[] }>(`/api/admin/staff/${staffId}/services`, {
      method: 'PUT',
      body: JSON.stringify({ serviceIds }),
    }),
  updateProfile: (staffId: string, data: StaffProfileUpdate) =>
    apiFetch<undefined>(`/api/admin/staff/${staffId}/profile`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Superadmin-only kill switch para la sync Google Calendar del staff
   *  (despidos). Soft-disable: el OAuth token row se mantiene, pero los
   *  push se saltan. Re-enable revierte. */
  setGoogleSyncDisabled: (staffId: string, disabled: boolean) =>
    apiFetch<{ id: string; googleSyncDisabled: boolean }>(`/api/admin/staff/${staffId}/google-sync`, {
      method: 'POST',
      body: JSON.stringify({ disabled }),
    }),
};

/** publicProfileEnabled solo lo aplica server-side el superadmin. */
export interface StaffProfileUpdate {
  name?: string | null;
  lastName?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  bio?: string | null;
  credentials?: string[] | null;
  functions?: string[] | null;
  websiteUrl?: string | null;
  publicProfileEnabled?: boolean;
}

// ---------------------------------------------------------------------------
// Admin rooms
// ---------------------------------------------------------------------------
export const adminRoomsApi = {
  list: () => apiFetch<Room[]>('/api/admin/rooms'),
  create: (data: { name: string }) =>
    apiFetch<Room>('/api/admin/rooms', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; isActive?: boolean }) =>
    apiFetch<Room>(`/api/admin/rooms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiFetch<undefined>(`/api/admin/rooms/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Admin centers
// ---------------------------------------------------------------------------
export const adminCentersApi = {
  list: () => apiFetch<unknown>('/api/admin/centers'),
  overview: () => apiFetch<CentersOverview>('/api/admin/centers/overview'),
  getMe: () => apiFetch<Center>('/api/admin/centers/me'),
  updateMe: (data: Record<string, unknown>) =>
    apiFetch<unknown>('/api/admin/centers/me', { method: 'PUT', body: JSON.stringify(data) }),
  create: (data: Record<string, unknown>) =>
    apiFetch<unknown>('/api/admin/centers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/admin/centers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiFetch<unknown>(`/api/admin/centers/${id}`, { method: 'DELETE' }),
  ...centerAdminExtrasApi,
};

// Re-export de los nuevos APIs (tags / profile / platform / grants admin /
// PR #4). Las definiciones viven en `./admin-extras.ts` para mantener
// `client.ts` bajo el límite de líneas del proyecto. Misma superficie de import.
export { centerAdminExtrasApi, adminTagsApi, adminPlatformApi, adminProfileApi, adminStaffPhotoApi, adminBookingsExtrasApi, adminCustomersSearchApi, adminAvailabilityApi } from './admin-extras';
export type { CenterAdmin, Tag, MyProfile, PublicDirectoryInfo, AdminBookingListParams, AdminCreateBookingBody, PublicSlotDto } from './admin-extras';
import { centerAdminExtrasApi } from './admin-extras';

// ---------------------------------------------------------------------------
// Admin audit log
// ---------------------------------------------------------------------------
export const adminAuditApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch<unknown>(`/api/admin/audit-log?${qs}`);
  },
};

// ---------------------------------------------------------------------------
// Admin slot overrides
// ---------------------------------------------------------------------------
export const adminSlotOverridesApi = {
  list: (from?: string) => {
    const qs = from ? `?from=${encodeURIComponent(from)}` : '';
    return apiFetch<SlotOverride[]>(`/api/admin/slot-overrides${qs}`);
  },
  create: (data: { startTime: string; endTime: string; staffId?: string; reason?: string }) =>
    apiFetch<CreateSlotOverrideResponse>('/api/admin/slot-overrides', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiFetch<undefined>(`/api/admin/slot-overrides/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Admin staff absences
// ---------------------------------------------------------------------------
export interface CreateStaffAbsencePayload {
  staffId: string;
  startDate: string;
  endDate: string;
  reason: StaffAbsenceReason;
  notes?: string;
}

export interface UpdateStaffAbsencePayload {
  startDate?: string;
  endDate?: string;
  reason?: StaffAbsenceReason;
  notes?: string | null;
}

export const adminStaffAbsencesApi = {
  list: (params?: { staffId?: string; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.staffId) qs.set('staffId', params.staffId);
    if (params?.from)    qs.set('from',    params.from);
    if (params?.to)      qs.set('to',      params.to);
    const suffix = qs.toString();
    return apiFetch<StaffAbsence[]>(
      `/api/admin/staff-absences${suffix ? `?${suffix}` : ''}`,
    );
  },
  create: (data: CreateStaffAbsencePayload) =>
    apiFetch<StaffAbsence>('/api/admin/staff-absences', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateStaffAbsencePayload) =>
    apiFetch<StaffAbsence>(`/api/admin/staff-absences/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiFetch<undefined>(`/api/admin/staff-absences/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Admin Google Calendar (PR2 mock-only) → vive en `./google.ts` (split para
// respetar el límite max-lines).
// ---------------------------------------------------------------------------
export { adminGoogleApi, type GoogleStatus } from './google';

// ---------------------------------------------------------------------------
// Public booking widget
// ---------------------------------------------------------------------------
export const publicApi = {
  getCenterTheme: (slug: string) =>
    apiFetch<unknown>(`/api/public/centers/${slug}/theme`, { credentials: 'omit' }),
  getCenterServices: (slug: string) =>
    apiFetch<unknown>(`/api/public/centers/${slug}/services`, { credentials: 'omit' }),
  getCenterStaff: (slug: string, serviceId: string) =>
    apiFetch<unknown>(`/api/public/centers/${slug}/staff?serviceId=${serviceId}`, {
      credentials: 'omit',
    }),
  getAvailability: (slug: string, params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch<unknown>(`/api/public/centers/${slug}/availability?${qs}`, {
      credentials: 'omit',
    });
  },
  createBooking: (slug: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/public/centers/${slug}/bookings`, {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'omit',
    }),
  getBookingByTrackingToken: (token: string) =>
    apiFetch<unknown>(`/api/public/bookings/${token}`, { credentials: 'omit' }),
  cancelByTrackingToken: (token: string, reason?: string) =>
    apiFetch<unknown>(`/api/public/bookings/${token}/cancel`, {
      method: 'POST',
      // Server validates body with Zod (cancelBookingSchema). Even though every
      // field is optional, an empty body fails `c.req.json()` → 422, which
      // silently aborts the cancellation. Always send valid JSON.
      body: JSON.stringify(reason !== undefined ? { reason } : {}),
      credentials: 'omit',
    }),
  rescheduleByTrackingToken: (token: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/api/public/bookings/${token}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'omit',
    }),
  confirmBooking: (confirmationToken: string) =>
    apiFetch<{ bookingId: string; centerId: string; status: string }>(
      '/api/public/bookings/confirm',
      {
        method: 'POST',
        body: JSON.stringify({ confirmationToken }),
        credentials: 'omit',
      },
    ),
};
